import { isBedbankItem, isInstantBookingLEHotelItem } from 'lib/checkout/checkoutUtils'
import config from 'constants/config'
import { OFFER_TYPE_BED_BANK, OFFER_TYPE_HOTEL } from 'constants/offer'
import { isEnabledForFeature } from 'lib/config/featureFlagUtils'
import { isFlashHotelOffer } from 'lib/offer/offerTypes'
import { groupBy, sortByAll } from 'lib/array/arrayUtils'

const ROOM_GUEST_FIELD_PREFIX = 'checkoutRoomGuest'

export type RoomGuestItem = App.Checkout.BedbankHotelItem | App.Checkout.InstantBookingLEHotelItem

function isRoomGuestFieldsOfferType(offerType: App.OfferType | undefined) {
  return !!offerType &&
    config.MULTIPLE_ROOM_TYPE_BOOKING_ENABLED &&
    isEnabledForFeature(config.MULTIPLE_ROOM_TYPE_BOOKING_ENABLED_LIST, offerType)
}

export function isRoomGuestFieldsOffer(offer: App.AnyOffer | undefined) {
  const offerType = offer?.type
  if (!isRoomGuestFieldsOfferType(offerType)) return false

  return offerType !== OFFER_TYPE_HOTEL || isFlashHotelOffer(offer)
}

export function getRoomGuestFieldKeys(itemId: string) {
  return {
    firstName: `${ROOM_GUEST_FIELD_PREFIX}_${itemId}_firstName`,
    lastName: `${ROOM_GUEST_FIELD_PREFIX}_${itemId}_lastName`,
  }
}

export function isRoomGuestFieldKey(fieldKey: string) {
  return fieldKey.startsWith(ROOM_GUEST_FIELD_PREFIX)
}

export function normaliseRoomGuestName(value: unknown) {
  if (typeof value !== 'string') return ''
  return value.trim().toLocaleLowerCase()
}

export interface RoomGuestPropertyMetadata {
  roomCount: number
  roomNumber: number
}

function getRoomGuestPropertyGroupKey(item: RoomGuestItem): string {
  return `${item.offerId}-${item.checkIn}-${item.checkOut}`
}

export function getRoomGuestPropertyMetadataByItemId(
  roomGuestItems: Array<RoomGuestItem>,
): Map<string, RoomGuestPropertyMetadata> {
  const countsByGroup = new Map<string, number>()
  for (const item of roomGuestItems) {
    const groupKey = getRoomGuestPropertyGroupKey(item)
    countsByGroup.set(groupKey, (countsByGroup.get(groupKey) ?? 0) + 1)
  }

  const roomNumbersByGroup = new Map<string, number>()
  const result = new Map<string, RoomGuestPropertyMetadata>()
  for (const item of roomGuestItems) {
    const groupKey = getRoomGuestPropertyGroupKey(item)
    const roomNumber = (roomNumbersByGroup.get(groupKey) ?? 0) + 1
    roomNumbersByGroup.set(groupKey, roomNumber)
    result.set(item.itemId, {
      roomCount: countsByGroup.get(groupKey) ?? 1,
      roomNumber,
    })
  }

  return result
}

export function getRoomGuestItemHotelName(
  item: RoomGuestItem,
  offers: Record<string, App.AnyOffer | undefined>,
  bedbankOffers: Record<string, App.BedbankOffer | undefined>,
): string {
  if (isBedbankItem(item)) {
    const offer = bedbankOffers[item.offerId]
    return offer?.property?.name ?? offer?.name ?? ''
  }

  if (isInstantBookingLEHotelItem(item)) {
    const offer = offers[item.offerId]
    if (!offer) return ''
    if ('property' in offer && offer.property?.name) {
      return offer.property.name
    }
    if ('name' in offer && typeof offer.name === 'string') {
      return offer.name
    }
  }

  return ''
}

export function getRoomGuestNames({
  itemId,
  primaryForm,
  fallbackFirstName,
  fallbackLastName,
  isEnabled,
  isPrimaryRoom,
}: {
  itemId: string
  primaryForm: App.Checkout.TravellerForm | undefined
  fallbackFirstName: string | undefined
  fallbackLastName: string | undefined
  isEnabled: boolean
  isPrimaryRoom: boolean
}) {
  if (!isEnabled) {
    return {
      firstName: fallbackFirstName,
      lastName: fallbackLastName,
    }
  }

  const fieldKeys = getRoomGuestFieldKeys(itemId)

  return {
    firstName: primaryForm?.data[fieldKeys.firstName] ?? (isPrimaryRoom ? fallbackFirstName : undefined),
    lastName: primaryForm?.data[fieldKeys.lastName] ?? (isPrimaryRoom ? fallbackLastName : undefined),
  }
}

export function getRoomGuestItems(
  items: Array<App.Checkout.AccommodationItem>,
  offers: Record<string, App.AnyOffer | undefined>,
): Array<RoomGuestItem> {
  const roomGuestItems = items.filter((item): item is RoomGuestItem => {
    return (isBedbankItem(item) && isRoomGuestFieldsOfferType(OFFER_TYPE_BED_BANK)) ||
      (isInstantBookingLEHotelItem(item) && isRoomGuestFieldsOffer(offers[item.offerId]))
  })

  return sortRoomGuestItemsByCartDisplayOrder(roomGuestItems)
}

function getRoomGuestItemBookingKey(item: RoomGuestItem) {
  return `${item.offerId}-${item.checkIn}-${item.checkOut}`
}

export function sortRoomGuestItemsByCartDisplayOrder(items: Array<RoomGuestItem>): Array<RoomGuestItem> {
  const itemGroups = Array.from(groupBy(items, getRoomGuestItemBookingKey).values()).map((items, index) => ({
    index,
    items,
    startDate: items[0].checkIn,
  }))

  return sortByAll(itemGroups, [
    { selector: itemGroup => itemGroup.startDate, direction: 'asc' },
    { selector: itemGroup => itemGroup.index, direction: 'asc' },
  ]).flatMap(itemGroup => itemGroup.items)
}

export function preserveRoomGuestFieldValues({
  previousData,
  nextData,
}: {
  previousData: App.Checkout.TravellerFormData
  nextData: App.Checkout.TravellerFormData
}): App.Checkout.TravellerFormData {
  const roomGuestData = Object.fromEntries(
    Object.entries(previousData).filter(([fieldKey]) => {
      return isRoomGuestFieldKey(fieldKey)
    }),
  ) as App.Checkout.TravellerFormData

  return {
    ...roomGuestData,
    ...nextData,
  }
}
